import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-scroll';
import { 
  ChevronDown, 
  ChevronRight, 
  Menu, 
  X
} from 'lucide-react';
import { navItems } from '../../data/NavItems';

export default function DeveloperDocumentation() {
  const [expandedItems, setExpandedItems] = useState<string[]>(['getting-started']);

   // Define el estado inicial basándose en el tamaño de la pantalla
   const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);

   useEffect(() => {
     const handleResize = () => {
       const isDesktop = window.innerWidth >= 1024;
       // Solo actualiza el estado si cambia de desktop a móvil o viceversa
       setIsSidebarOpen((prev) => (prev !== isDesktop ? isDesktop : prev));
     };
 
     window.addEventListener("resize", handleResize);
 
     // Limpia el event listener para evitar fugas de memoria
     return () => window.removeEventListener("resize", handleResize);
   }, []);

  const toggleExpand = (id: string) => {
    setExpandedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const CodeBlock = ({ children }: { children: string }) => (
    <motion.div 
      className="bg-blue-50 rounded-lg p-4 border border-blue-100 mt-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <code className="text-blue-700 font-mono text-sm">{children}</code>
    </motion.div>
  );

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <section id={id} className="mb-20">
      <motion.h2 
        className="text-2xl font-semibold text-[#1e3a8a] mb-8 relative pl-4 border-l-4 border-blue-200"
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        {title}
      </motion.h2>
      <div className="space-y-6">
        {children}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Menu Button */}
      <motion.button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-[4.3rem] left-4 z-50 p-2 bg-white border border-blue-100 rounded-lg shadow-sm lg:hidden"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isSidebarOpen ? (
          <X className="w-6 h-6 text-blue-600" />
        ) : (
          <Menu className="w-6 h-6 text-blue-600" />
        )}
      </motion.button>

      {/* Sidebar */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="fixed top-0 left-0 h-screen w-72 bg-white border-r scrollbar-hidden border-blue-50 shadow-lg overflow-y-auto z-40"
          >
            <div className="p-6">
              <h2 className="text-xl font-bold mt-24 scrollbar-hidden text-[#1e3a8a] mb-6">Developer Guide</h2>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <div key={item.id}>
                    <motion.button
                      onClick={() => toggleExpand(item.id)}
                      className="w-full flex items-center justify-between p-3 text-[#1c3d6e] hover:bg-blue-50 rounded-lg transition-all"
                      whileHover={{ x: 5 }}
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon className="w-5 h-5 text-blue-600" />
                        <span>{item.title}</span>
                      </div>
                      <motion.div
                        animate={{ rotate: expandedItems.includes(item.id) ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-4 h-4 text-blue-600" />
                      </motion.div>
                    </motion.button>
                    <AnimatePresence>
                      {expandedItems.includes(item.id) && item.subItems && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-8 mt-1 space-y-1"
                        >
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.id}
                              to={subItem.id}
                              spy={true}
                              smooth={true}
                              offset={-100}
                              duration={500}
                              className="flex items-center space-x-2 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                              onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)}
                            >
                              <ChevronRight className="w-4 h-4 text-blue-400" />
                              <span>{subItem.title}</span>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </nav>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : 'ml-0'}`}>
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Section id="installation" title="Installation">
            <p className="text-[#1c3d6e] leading-relaxed">
              Get started with our templates using your preferred package manager:
            </p>
            <CodeBlock>npm install @templatehub/templates</CodeBlock>
            <p className="text-[#1c3d6e] mt-6 leading-relaxed">
              Or clone the repository directly:
            </p>
            <CodeBlock>git clone https://github.com/templatehub/core.git</CodeBlock>
          </Section>

          <Section id="prerequisites" title="Prerequisites">
            <p className="text-[#1c3d6e] leading-relaxed">
              Before you begin, ensure you have the following installed:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#1c3d6e] mt-4">
              <li>Node.js (version 18 or higher)</li>
              <li>npm (version 9+) or yarn (version 1.22+)</li>
              <li>Git (version 2.34+)</li>
            </ul>
          </Section>

          <Section id="project-structure" title="Project Structure">
            <p className="text-[#1c3d6e] leading-relaxed">
              Our templates follow a consistent and scalable project structure:
            </p>
            <CodeBlock>{`template-project/
├── src/
│   ├── components/   # Reusable components
│   ├── hooks/        # Custom React hooks
│   ├── pages/        # Application routes
│   ├── styles/       # Global styles & theme
│   └── utils/        # Helper functions
├── public/           # Static assets
├── tests/            # Test suites
└── config/           # Build configurations`}</CodeBlock>
          </Section>

          <Section id="design-patterns" title="Design Patterns">
            <p className="text-[#1c3d6e] leading-relaxed">
              We implement proven architectural patterns:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#1c3d6e] mt-4">
              <li>Component-Driven Architecture</li>
              <li>Atomic Design Principles</li>
              <li>Dependency Injection</li>
              <li>Observer Pattern</li>
            </ul>
          </Section>

          <Section id="optimization" title="Performance Optimization">
            <p className="text-[#1c3d6e] leading-relaxed">
              Core optimization strategies:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#1c3d6e] mt-4">
              <li>Dynamic Import & Code Splitting</li>
              <li>Advanced Caching Mechanisms</li>
              <li>Bundle Analysis & Tree Shaking</li>
              <li>Lazy Loading & Suspense</li>
            </ul>
            <motion.div 
              className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
            >
              <h4 className="text-[#1e3a8a] font-semibold mb-2">Best Practice</h4>
              <p className="text-[#1c3d6e]">
                Implement performance budgets and monitor bundle sizes during development.
              </p>
            </motion.div>
          </Section>

          <Section id="authentication" title="Authentication">
            <p className="text-[#1c3d6e] leading-relaxed">
              Secure authentication flow implementation:
            </p>
            <CodeBlock>{`import { AuthProvider, useAuth } from '@auth/core';

function App() {
  return (
    <AuthProvider>
      <ProtectedRoute />
    </AuthProvider>
  );
}`}</CodeBlock>
          </Section>

          <Section id="db-setup" title="Database Configuration">
            <p className="text-[#1c3d6e] leading-relaxed">
              Environment-based database configuration:
            </p>
            <CodeBlock>{`// .env.example
DB_HOST=your_database_host
DB_PORT=5432
DB_USER=secure_username
DB_PASSWORD=strong_password
DB_NAME=production_db`}</CodeBlock>
            <motion.div 
              className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
            >
              <h4 className="text-[#1e3a8a] font-semibold mb-2">Security Note</h4>
              <p className="text-[#1c3d6e]">
                Always encrypt sensitive credentials and use IAM roles for production databases.
              </p>
            </motion.div>
          </Section>

          <Section id="migrations" title="Database Migrations">
            <p className="text-[#1c3d6e] leading-relaxed">
              Schema migration example:
            </p>
            <CodeBlock>{`exports.up = knex => knex.schema.createTable('users', table => {
  table.uuid('id').primary();
  table.string('email').unique().notNullable();
  table.string('password_hash').notNullable();
  table.timestamp('created_at').defaultTo(knex.fn.now());
  table.timestamp('updated_at').defaultTo(knex.fn.now());
});`}</CodeBlock>
          </Section>
        </div>
      </main>
    </div>
  );
}